import {
  Box,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Theme,
  Typography,
} from '@mui/material';
import { ImgHTMLAttributes } from 'react';

import { getAssetPath } from '@/constants/configs';

export const StyledRadio = styled(Radio)(({ theme }) => ({
  '&.Mui-checked': {
    color: theme.palette.primaries?.[500] ?? theme.palette.primary.main,
  },
}));

export const QuestionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  maxWidth: 358,
  width: '100%',
  alignSelf: 'center',
  paddingBottom: theme.spacing(8),
}));

export const PEPQuestionLogo = styled(
  (props: ImgHTMLAttributes<HTMLImageElement>) => (
    <img src={getAssetPath('/logo-with-text.svg')} {...props} />
  ),
)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: 109,
}));

export const ProtectedImg = styled(
  (props: ImgHTMLAttributes<HTMLImageElement>) => (
    <img src={getAssetPath('/protected.png')} {...props} />
  ),
)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(6.5),
  width: 218,
}));

export const USIndiciaImage = styled(
  (props: ImgHTMLAttributes<HTMLImageElement>) => (
    <img src={getAssetPath('/question-us-indicia.svg')} {...props} />
  ),
)(({ theme }) => ({
  margin: `${theme.spacing(7.5)} auto ${theme.spacing(7.5)}`,
  width: 200,
}));

export const DualCitizenshipImage = styled(
  (props: ImgHTMLAttributes<HTMLImageElement>) => (
    <img
      src={getAssetPath('/dual-citizenship/question-dual-citizenship-bvi.svg')}
      {...props}
    />
  ),
)(({ theme }) => ({
  margin: `${theme.spacing(7.5)} auto ${theme.spacing(7.5)}`,
  width: 290,
}));

export const HintIconWrapper = styled('span')(({ theme }) => ({
  display: 'inline-block',
  transform: `translateY(${theme.spacing(0.5)})`,
}));

export const ToolTipWrapper = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
}));

const richTextLayout = (theme: Theme) => ({
  textAlign: 'left' as const,
  '& p': {
    margin: 0,
  },
  '& p + p, & ul + p': {
    marginTop: theme.spacing(1.5),
  },
  '& ul': {
    listStyleType: 'disc',
    margin: `${theme.spacing(0.5)} 0 0`,
    paddingLeft: theme.spacing(3),
  },
});

export const QuestionRichText = styled(Box)(({ theme }) => ({
  ...richTextLayout(theme),
  ...theme.typography.paragraphP2,
  color: theme.palette.neutral[800],
  '& p': {
    margin: 0,
    ...theme.typography.bodyB2Medium,
    color: theme.palette.text.primary,
  },
}));

export const TooltipRichText = styled(Box)(({ theme }) => ({
  ...richTextLayout(theme),
  color: 'inherit',
}));

export const SeeMoreDetailsButton = styled('button')(({ theme }) => ({
  ...theme.typography.bodyB2Strong,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  alignSelf: 'flex-end',
  padding: 0,
  border: 'none',
  background: 'none',
  color: theme.palette.text.primary,
  cursor: 'pointer',
}));

export const FundingCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.neutral[100],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
}));

export const FundingCardInput = styled(Box)(({ theme }) => ({
  '.MuiOutlinedInput-root': {
    paddingLeft: theme.spacing(0),
    '.MuiInputBase-inputAdornedStart': {
      paddingLeft: theme.spacing(0),
    },
  },
}));

export const CardSpacer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const AmountQuestion = styled('div')(({ theme }) => ({
  ...theme.typography.body1Medium,
  marginBottom: theme.spacing(1),
}));

export const InlineCurrencySelectWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: theme.spacing(0),
  '.MuiFormControl-root': {
    minWidth: 'auto',
  },
  '.MuiOutlinedInput-root fieldset': {
    border: 0,
  },
  '.MuiListItemText-root': {
    marginTop: theme.spacing(0.25),
  },
}));

export const QuestionSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.neutral[100],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  width: '100%',
}));

export const QuestionTitle = styled(Typography)(({ theme }) => ({
  lineHeight: 1.35,
  color: theme.palette.text.primary,
  textAlign: 'left',
  width: '100%',
}));

export const RadioButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  '.MuiFormControlLabel-root': {
    margin: 0,
  },
}));

export const RadioButtonContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.neutral[200]}`,
  padding: theme.spacing(1.5, 2),
  width: '100%',
}));

export const QuestionBlocks = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  rowGap: theme.spacing(2.5),
}));

export const QuestionBlock = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  rowGap: theme.spacing(1.5),
}));

export const NestedQuestionBlocks = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  rowGap: theme.spacing(2.5),
  marginTop: theme.spacing(2),
}));

export const UsernameContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  rowGap: theme.spacing(1),
  '& > span.MuiBox-root': {
    marginTop: `${theme.spacing(0.5)} !important`,
    overflow: 'visible',
  },
}));

export const FullWidthRadioGroup = styled(RadioGroup)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  columnGap: theme.spacing(2),
  '.MuiFormControlLabel-root': {
    flex: 1,
    margin: 0,
    justifyContent: 'flex-start',
  },
}));
